import { Card, Col, Row, Statistic, Typography, Tag, Space, Spin } from 'antd';
import {
  DollarOutlined,
  CreditCardOutlined,
  TeamOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { trpc } from '../../lib/api';
import { useAgencyAuthStore } from '../../stores/agencyAuthStore';
import { ResponsiveTable, type ResponsiveColumnType } from '../../components/common/responsive';
import { PageHeader, PageContainer } from '../../components/common';

type CouponRow = {
  id: string;
  couponCode: string;
  couponType: string;
  discountType: 'percentage' | 'fixed_amount';
  discountValue: string;
  usageCount: number;
  isActive: boolean;
};

type CommissionRow = {
  id: string;
  periodStart: string;
  periodEnd: string;
  totalSales: string;
  commissionAmount: string;
  paymentStatus: 'pending' | 'prepared' | 'paid' | 'canceled' | string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeCouponRow = (value: unknown): CouponRow | null => {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }

  return {
    id: value.id,
    couponCode: typeof value.couponCode === 'string' ? value.couponCode : '',
    couponType: typeof value.couponType === 'string' ? value.couponType : '',
    discountType: value.discountType === 'fixed_amount' ? 'fixed_amount' : 'percentage',
    discountValue: typeof value.discountValue === 'string' ? value.discountValue : '0',
    usageCount: typeof value.usageCount === 'number' ? value.usageCount : 0,
    isActive: typeof value.isActive === 'boolean' ? value.isActive : false,
  };
};

const normalizeCommissionRow = (value: unknown): CommissionRow | null => {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }

  return {
    id: value.id,
    periodStart: typeof value.periodStart === 'string' ? value.periodStart : '',
    periodEnd: typeof value.periodEnd === 'string' ? value.periodEnd : '',
    totalSales: typeof value.totalSales === 'string' ? value.totalSales : '0',
    commissionAmount: typeof value.commissionAmount === 'string' ? value.commissionAmount : '0',
    paymentStatus: typeof value.paymentStatus === 'string' ? value.paymentStatus : 'pending',
  };
};

export function AgencyDashboardPage() {
  const { agencyUser } = useAgencyAuthStore();

  // 統計情報取得
  const { isLoading: profileLoading } = trpc.agencyProfile.getStatistics.useQuery();
  const { data: couponStats, isLoading: couponLoading } = trpc.agencyCoupon.getStatistics.useQuery();
  const { data: commissionStats, isLoading: commissionLoading } = trpc.agencyCommission.getStatistics.useQuery();

  // クーポン一覧取得（最新5件）
  const { data: couponsData, isLoading: couponsListLoading } = trpc.agencyCoupon.listCoupons.useQuery({
    page: 1,
    limit: 5,
    isActive: true,
  });

  // 手数料一覧取得（最新5件）
  const { data: commissionsData, isLoading: commissionsListLoading } = trpc.agencyCommission.listCommissions.useQuery({
    page: 1,
    limit: 5,
  });
  const coupons = (couponsData?.coupons ?? []).flatMap((coupon) => {
    const normalized = normalizeCouponRow(coupon);
    return normalized ? [normalized] : [];
  });
  const commissions = (commissionsData?.commissions ?? []).flatMap((commission) => {
    const normalized = normalizeCommissionRow(commission);
    return normalized ? [normalized] : [];
  });

  const isLoading = profileLoading || couponLoading || commissionLoading;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const couponColumns: ResponsiveColumnType<CouponRow>[] = [
    {
      title: 'クーポンコード',
      dataIndex: 'couponCode',
      key: 'couponCode',
      render: (code: string) => <code>{code}</code>,
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: 'タイプ',
      dataIndex: 'couponType',
      key: 'couponType',
      mobileRole: 'tag',
      mobilePriority: 2,
    },
    {
      title: '割引',
      key: 'discount',
      render: (_, record) => {
        const value = parseFloat(record.discountValue);
        return record.discountType === 'percentage'
          ? `${value}%`
          : `¥${value.toLocaleString()}`;
      },
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: '使用回数',
      dataIndex: 'usageCount',
      key: 'usageCount',
      mobileRole: 'description',
      mobilePriority: 4,
    },
    {
      title: 'ステータス',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) =>
        isActive ? <Tag color="green">有効</Tag> : <Tag>無効</Tag>,
      mobileRole: 'tag',
      mobilePriority: 5,
    },
  ];

  const commissionColumns: ResponsiveColumnType<CommissionRow>[] = [
    {
      title: '期間',
      key: 'period',
      render: (_, record) => {
        const start = new Date(record.periodStart).toLocaleDateString('ja-JP');
        const end = new Date(record.periodEnd).toLocaleDateString('ja-JP');
        return `${start} - ${end}`;
      },
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: '総売上',
      dataIndex: 'totalSales',
      key: 'totalSales',
      render: (sales) => `¥${parseFloat(sales).toLocaleString()}`,
      mobileRole: 'description',
      mobilePriority: 2,
    },
    {
      title: '手数料額',
      dataIndex: 'commissionAmount',
      key: 'commissionAmount',
      render: (amount) => `¥${parseFloat(amount).toLocaleString()}`,
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: 'ステータス',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (status: string) => {
        const statusMap: Record<string, { color: string; label: string }> = {
          pending: { color: 'orange', label: '未払い' },
          prepared: { color: 'blue', label: '準備完了' },
          paid: { color: 'green', label: '支払済み' },
          canceled: { color: 'red', label: 'キャンセル' },
        };
        const info = statusMap[status] || { color: 'default', label: status };
        return <Tag color={info.color}>{info.label}</Tag>;
      },
      mobileRole: 'tag',
      mobilePriority: 4,
    },
  ];

  return (
    <PageContainer>
      <PageHeader title="ダッシュボード" />

      <Card style={{ marginBottom: '24px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Space direction="vertical" size="small">
          <Typography.Text style={{ color: 'white', fontSize: '16px' }}>
            ようこそ、{agencyUser?.name}様
          </Typography.Text>
          <Typography.Text style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            クーポンプレフィックス: <strong>{agencyUser?.couponPrefix}</strong>
          </Typography.Text>
          {agencyUser?.companyName && (
            <Typography.Text style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {agencyUser.companyName}
            </Typography.Text>
          )}
        </Space>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="総クーポン数"
              value={couponStats?.totalCoupons || 0}
              prefix={<CreditCardOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="アクティブクーポン"
              value={couponStats?.activeCoupons || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="アクティブ顧客"
              value={couponStats?.activeCustomers || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="未払い手数料"
              value={commissionStats?.pendingCommission || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#cf1322' }}
              precision={0}
              suffix="円"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card title="最近のクーポン" extra={<a href="/agency/coupons">すべて見る</a>}>
            <ResponsiveTable<CouponRow>
              dataSource={coupons}
              columns={couponColumns}
              loading={couponsListLoading}
              pagination={false}
              rowKey="id"
              size="small"
              mobileCardConfig={{
                titleField: 'couponCode',
              }}
            />
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card title="最近の手数料" extra={<a href="/agency/commissions">すべて見る</a>}>
            <ResponsiveTable<CommissionRow>
              dataSource={commissions}
              columns={commissionColumns}
              loading={commissionsListLoading}
              pagination={false}
              rowKey="id"
              size="small"
              mobileCardConfig={{
                titleField: 'periodStart',
              }}
            />
          </Card>
        </Col>
      </Row>
    </PageContainer>
  );
}
